import { useState, useRef } from "react";
import {
  CheckCircle2,
  Download,
  Paperclip,
  Send,
  AlertCircle,
  ImagePlus,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DisputeDto, DisputeStatus } from "@shared/types/DisputeDto";
import { useDisputes } from "@/providers/DisputeProvider";

const MAX_IMAGES = 4;
const MAX_IMAGE_SIZE = 500 * 1024;
const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/jpg"];

interface DisputeDetailModalProps {
  dispute: DisputeDto | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const getStatusColor = (status: DisputeStatus) => {
  switch (status) {
    case "OPEN":
      return "bg-blue-500/20 text-blue-400 border-blue-500/30";
    case "UNDER_REVIEW":
      return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
    case "AWAITING_CLIENT_RESPONSE":
      return "bg-purple-500/20 text-purple-400 border-purple-500/30";
    case "IN_MEDIATION":
      return "bg-orange-500/20 text-orange-400 border-orange-500/30";
    case "RESOLVED":
      return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
    case "CLOSED":
      return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    default:
      return "bg-gray-500/20 text-gray-400 border-gray-500/30";
  }
};

const getProgressSteps = (status: DisputeStatus) => {
  const allSteps = [
    "Opened",
    "Evidence Review",
    "Negotiation",
    "Final Resolution",
  ];
  let currentStep = 0;

  switch (status) {
    case "OPEN":
      currentStep = 0;
      break;
    case "UNDER_REVIEW":
      currentStep = 1;
      break;
    case "AWAITING_CLIENT_RESPONSE":
      currentStep = 2;
      break;
    case "IN_MEDIATION":
      currentStep = 2;
      break;
    case "RESOLVED":
      currentStep = 3;
      break;
    case "CLOSED":
      currentStep = 3;
      break;
  }

  return { steps: allSteps, current: currentStep };
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

export function DisputeDetailModal({
  dispute,
  open,
  onOpenChange,
}: DisputeDetailModalProps) {
  const { addComment, acceptResolution, escalateToMediation } = useDisputes();
  const [newComment, setNewComment] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!dispute) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles: File[] = [];
    const previews: string[] = [];

    for (const file of files) {
      if (selectedFiles.length + validFiles.length >= MAX_IMAGES) break;
      if (!ALLOWED_TYPES.includes(file.type)) continue;
      if (file.size > MAX_IMAGE_SIZE) continue;
      validFiles.push(file);
      previews.push(URL.createObjectURL(file));
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);
    setFilePreviews((prev) => [...prev, ...previews]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (index: number) => {
    URL.revokeObjectURL(filePreviews[index]);
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setFilePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddComment = async () => {
    if (!newComment.trim() && selectedFiles.length === 0) return;
    setIsSending(true);
    try {
      await addComment(
        dispute.id,
        newComment,
        selectedFiles.length > 0 ? selectedFiles : undefined
      );
      setNewComment("");
      filePreviews.forEach((url) => URL.revokeObjectURL(url));
      setSelectedFiles([]);
      setFilePreviews([]);
    } catch (error) {
      console.error("Failed to add comment:", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleAcceptResolution = async () => {
    try {
      await acceptResolution(dispute.id);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to accept resolution:", error);
    }
  };

  const handleEscalate = async () => {
    try {
      await escalateToMediation(dispute.id);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to escalate:", error);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto glass-card">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle className="text-2xl mb-2">
                  {dispute.title}
                </DialogTitle>
                <div className="flex items-center gap-2">
                  <Badge className={getStatusColor(dispute.status)}>
                    {dispute.status.replace(/_/g, " ")}
                  </Badge>
                  <span className="text-sm text-muted-foreground">•</span>
                  <span className="text-sm text-muted-foreground">
                    Project: {dispute.projectId}
                  </span>
                </div>
              </div>
            </div>
          </DialogHeader>

          <div className="mt-6 space-y-6">
            <div>
              <h4 className="font-semibold mb-3">Progress</h4>
              <div className="flex items-center justify-between">
                {getProgressSteps(dispute.status).steps.map((step, idx) => (
                  <div key={idx} className="flex items-center flex-1">
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          idx <= getProgressSteps(dispute.status).current
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {idx < getProgressSteps(dispute.status).current ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : (
                          <span className="text-xs">{idx + 1}</span>
                        )}
                      </div>
                      <span className="text-xs mt-2 text-center">{step}</span>
                    </div>
                    {idx < getProgressSteps(dispute.status).steps.length - 1 && (
                      <div
                        className={`h-0.5 flex-1 mx-2 ${
                          idx < getProgressSteps(dispute.status).current
                            ? "bg-primary"
                            : "bg-muted"
                        }`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {dispute.resolutionSummary && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <h4 className="font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5" />
                  Resolution Summary
                </h4>
                <p className="text-sm">{dispute.resolutionSummary}</p>
                {dispute.refundIssued && (
                  <Badge className="mt-2 bg-emerald-500/20 text-emerald-400">
                    Refund Issued
                  </Badge>
                )}
              </div>
            )}

            <div>
              <h4 className="font-semibold mb-2">Description</h4>
              <p className="text-sm text-muted-foreground bg-muted/30 p-4 rounded-lg">
                {dispute.description}
              </p>
            </div>

            {dispute.disputedAmount && (
              <div>
                <h4 className="font-semibold mb-2">Disputed Amount</h4>
                <p className="text-2xl font-bold text-amber-400">
                  {dispute.currency} ${dispute.disputedAmount.toLocaleString()}
                </p>
              </div>
            )}

            {dispute.attachments.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3">Evidence Attachments</h4>
                <div className="grid grid-cols-2 gap-3">
                  {dispute.attachments.map((att) => (
                    <div
                      key={att.id}
                      className="flex items-center gap-3 p-3 border border-border/50 rounded-lg hover:bg-muted/20 transition-colors"
                    >
                      <Paperclip className="h-5 w-5 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {att.fileName}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(att.fileSizeInBytes)} •{" "}
                          {att.uploadedAt.toLocaleDateString()}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm">
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h4 className="font-semibold mb-3">Communication History</h4>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {dispute.history
                  .filter((msg) => !msg.isInternalOnly)
                  .map((msg) => (
                    <div
                      key={msg.id}
                      className={`p-4 rounded-lg ${
                        msg.authorRole === "CLIENT"
                          ? "bg-primary/10 ml-0 mr-12"
                          : "bg-muted/30 ml-12 mr-0"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge variant="outline" className="text-xs">
                          {msg.authorRole}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {msg.timestamp.toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm">{msg.content}</p>
                      {msg.imageAttachments && msg.imageAttachments.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {msg.imageAttachments.map((img) => (
                            <button
                              key={img.id}
                              type="button"
                              onClick={() => setLightboxImage(img.imageUrl)}
                              className="relative group rounded-lg overflow-hidden border border-border/50 hover:border-primary/50 transition-colors"
                            >
                              <img
                                src={img.imageUrl}
                                alt={img.fileName}
                                className="w-24 h-24 object-cover"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
              </div>

              <div className="mt-4 space-y-2">
                {selectedFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-2 bg-muted/20 rounded-lg">
                    {filePreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview}
                          alt={selectedFiles[index].name}
                          className="w-16 h-16 object-cover rounded-md border border-border/50"
                        />
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                        <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-[9px] text-white text-center truncate px-0.5">
                          {formatFileSize(selectedFiles[index].size)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".png,.jpg,.jpeg"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={selectedFiles.length >= MAX_IMAGES || isSending}
                    title={`Attach images (${selectedFiles.length}/${MAX_IMAGES})`}
                  >
                    <ImagePlus className="h-4 w-4" />
                  </Button>
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment..."
                    className="glass-input"
                    disabled={isSending}
                    onKeyPress={(e) => e.key === "Enter" && handleAddComment()}
                  />
                  <Button
                    onClick={handleAddComment}
                    className="gradient-primary"
                    disabled={isSending || (!newComment.trim() && selectedFiles.length === 0)}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  PNG or JPG, max 500KB each, up to {MAX_IMAGES} images
                </p>
              </div>
            </div>

            {dispute.status === "UNDER_REVIEW" && (
              <div className="flex gap-3 pt-4 border-t border-border/50">
                <Button
                  onClick={handleAcceptResolution}
                  className="gradient-primary flex-1"
                >
                  Accept Resolution
                </Button>
                <Button
                  onClick={handleEscalate}
                  variant="outline"
                  className="flex-1"
                >
                  <AlertCircle className="h-4 w-4 mr-2" />
                  Escalate to Mediation
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={lightboxImage !== null}
        onOpenChange={() => setLightboxImage(null)}
      >
        <DialogContent className="max-w-3xl p-2 bg-black/90 border-none">
          <DialogHeader className="sr-only">
            <DialogTitle>Image Preview</DialogTitle>
          </DialogHeader>
          {lightboxImage && (
            <img
              src={lightboxImage}
              alt="Full size preview"
              className="w-full h-auto max-h-[80vh] object-contain rounded"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
